import { CSSProperties, useEffect, useMemo, useRef, useState } from 'react'
import {
  Cell,
  ColumnDef,
  ColumnPinningState,
  flexRender,
  getCoreRowModel,
  Header,
  Row,
  Table,
  useReactTable,
} from '@tanstack/react-table'
import cn from './index.module.css'
import { Empty, Spin } from 'antd'
import classNames from 'classnames'
import { arrayMove, useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import {
  CopyOutlined,
  EyeInvisibleOutlined,
  InfoCircleOutlined,
  LoadingOutlined,
  MoreOutlined,
  SortAscendingOutlined,
  SortDescendingOutlined,
  SwapOutlined,
} from '@ant-design/icons'
import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  restrictToHorizontalAxis,
  restrictToVerticalAxis,
} from '@dnd-kit/modifiers'
import {
  SortableContext,
  horizontalListSortingStrategy,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import TooltipWrapper from '@pages/Admin/CourseSettings/components/TooltipWrapper/TooltipWrapper'
import Preloader from '@UI/Preloader'
import { RowDragHandleContext } from './rowDragContext'
import { useAddAlert } from '@/hooks/useAddAlert'

interface ISubHeader {
  pinned: 'top' | 'bottom'
  items: { key: string; value: string }[]
}

export type EditModeType = 'dnd' | 'visibility'

export interface IEditMode {
  active: boolean
  mode: EditModeType[]
  editOrderKey?: string[]
  editVisibilityKey?: string[]
  hiddenColumns?: string[]
  columnOrder?: string[]
  onChange: ({
    columnOrder,
    hiddenColumns,
  }: {
    columnOrder: string[]
    hiddenColumns: string[]
  }) => void
}

export interface IRowReorder<T = unknown> {
  active: boolean
  sortableRowIds: string[]
  onDragEnd: (event: DragEndEvent) => void
  constrainToGroup?: boolean
  getGroupId?: (row: T) => string | null | undefined
}

interface ICustomTable<T> {
  loading?: boolean
  data: T[]
  columns: ColumnDef<T, any>[]
  subHeader?: ISubHeader
  editMode?: IEditMode
  tooltips?: { id: string; tooltip: string }[]
  sorting?: { key: string; order: 'asc' | 'desc' | 'none' }[]
  onSortingClick?: (key: string) => void
  flexColumns?: boolean
  compact?: boolean
  dense?: boolean
  dragScroll?: boolean
  stickyHeader?: boolean
  className?: string
  headerBgColor?: string
  align?: 'left' | 'center'
  noHeaderWrap?: boolean
  firstColumnBg?: string
  hoverHeaderActions?: boolean
  showHeaderTooltipAction?: boolean
  overlayLoading?: boolean
  pinnedLeftColumns?: string[]
  pinnedRightColumns?: string[]
  getRowId?: (row: T, index: number) => string
  getRowClassName?: (row: T) => string | undefined
  rowReorder?: IRowReorder<T>
  copyColumns?: boolean
  copyColumnsMultiple?: boolean
  getCopyColumnValue?: (row: T, columnId: string, rowIndex: number) => unknown
}

type HeaderAction = {
  key: string
  title: string
  icon: JSX.Element
  active?: boolean
  tooltip?: string
  onClick?: () => void
}

const COPY_DISABLED_COLUMN_IDS = new Set([
  'select',
  'index',
  'student_avatar',
  'set-homework',
  'set-unique-homework',
  'add-lesson-record',
])
const COPY_COLUMNS_MULTI_SEPARATOR = ' -- '

const normalizeCopyValue = (value: unknown): string => {
  if (value === null || value === undefined || value === '') {
    return '-'
  }

  if (Array.isArray(value)) {
    return value.map(normalizeCopyValue).join(', ') || '-'
  }

  if (typeof value === 'object') {
    return '-'
  }

  return String(value).replace(/\s+/g, ' ').trim() || '-'
}

const copyTextToClipboard = async (text: string) => {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text)
    return
  }

  const textarea = document.createElement('textarea')
  textarea.value = text
  textarea.style.position = 'fixed'
  textarea.style.opacity = '0'
  document.body.appendChild(textarea)
  textarea.focus()
  textarea.select()
  document.execCommand('copy')
  document.body.removeChild(textarea)
}

const canCopyColumn = (columnId: string) =>
  !!columnId &&
  !COPY_DISABLED_COLUMN_IDS.has(columnId) &&
  !columnId.includes('action')

const CustomTable = <T,>({
  loading = false,
  data,
  columns,
  subHeader,
  editMode,
  tooltips = [],
  sorting = [],
  onSortingClick,
  flexColumns = false,
  compact = false,
  dense = false,
  dragScroll = false,
  stickyHeader = false,
  className,
  headerBgColor,
  align = 'center',
  noHeaderWrap = false,
  firstColumnBg,
  hoverHeaderActions = true,
  showHeaderTooltipAction = true,
  overlayLoading = false,
  pinnedLeftColumns = ['index'],
  pinnedRightColumns = [],
  getRowId,
  getRowClassName,
  rowReorder,
  copyColumns = false,
  copyColumnsMultiple = false,
  getCopyColumnValue,
}: ICustomTable<T>) => {
  const addAlert = useAddAlert()
  const tableWrapperRef = useRef<HTMLDivElement | null>(null)
  const dragScrollState = useRef({
    active: false,
    moved: false,
    startX: 0,
    scrollLeft: 0,
  })
  const [isDragScrolling, setIsDragScrolling] = useState(false)
  const [copyColumnIds, setCopyColumnIds] = useState<string[]>([])
  const [columnPinning, setColumnPinning] = useState<ColumnPinningState>({
    left: pinnedLeftColumns,
    right: pinnedRightColumns,
  })

  const pinnedLeftKey = pinnedLeftColumns.join('|')
  const pinnedRightKey = pinnedRightColumns.join('|')

  useEffect(() => {
    setColumnPinning({
      left: pinnedLeftColumns,
      right: pinnedRightColumns,
    })
  }, [pinnedLeftKey, pinnedRightKey])

  const columnVisibility = useMemo(() => {
    return columns.reduce<Record<string, boolean>>((acc, column) => {
      const visible = editMode?.active
        ? true
        : !editMode?.hiddenColumns?.includes(column.id ?? '')

      acc[column.id ?? ''] = visible
      return acc
    }, {})
  }, [editMode, columns])

  const columnOrder = useMemo(() => {
    const ordering = editMode?.columnOrder ?? []
    const listColumns = columns.map((c) => c.id!)
    const uniqueElements = listColumns.filter(
      (item) => !ordering.includes(item),
    )
    const newOrdering = ordering.concat(uniqueElements)
    return newOrdering
  }, [editMode, columns])

  const columnSizing = useMemo(
    () =>
      columns.reduce<Record<string, number>>((acc, column) => {
        const id = column.id
        const size = column.size

        if (id != null && typeof size === 'number') {
          acc[id] = dense
            ? Math.max(Math.round(size * 0.55), 30)
            : compact
              ? Math.max(Math.round(size * 0.75), 36)
              : size
        }

        return acc
      }, {}),
    [columns, compact, dense],
  )

  const handleDragScrollStart = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!dragScroll || event.button !== 0) return

    const target = event.target as HTMLElement
    if (target.closest('button, a, input, textarea, select, [role="button"]')) {
      return
    }

    const wrapper = tableWrapperRef.current
    if (!wrapper || wrapper.scrollWidth <= wrapper.clientWidth) return

    dragScrollState.current = {
      active: true,
      moved: false,
      startX: event.clientX,
      scrollLeft: wrapper.scrollLeft,
    }
    setIsDragScrolling(true)
  }

  const handleDragScrollMove = (event: React.MouseEvent<HTMLDivElement>) => {
    const wrapper = tableWrapperRef.current
    const dragState = dragScrollState.current
    if (!wrapper || !dragState.active) return

    const distance = event.clientX - dragState.startX
    if (Math.abs(distance) > 3) {
      dragState.moved = true
    }
    wrapper.scrollLeft = dragState.scrollLeft - distance
  }

  const handleDragScrollEnd = () => {
    if (!dragScrollState.current.active) return
    dragScrollState.current.active = false
    setIsDragScrolling(false)
  }

  const handleDragScrollClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!dragScrollState.current.moved) return
    event.preventDefault()
    event.stopPropagation()
    dragScrollState.current.moved = false
  }

  const table = useReactTable({
    data: data,
    columns,
    getRowId,
    getCoreRowModel: getCoreRowModel(),
    state: {
      columnOrder,
      columnPinning,
      columnVisibility,
      columnSizing,
    },
    onColumnPinningChange: setColumnPinning,
  })

  const sensors = useSensors(
    useSensor(MouseSensor, {}),
    useSensor(TouchSensor, {}),
    useSensor(KeyboardSensor, {}),
  )

  const rowReorderSensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: { distance: 6 },
    }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 120, tolerance: 6 },
    }),
    useSensor(KeyboardSensor, {}),
  )

  const tableRows = table.getRowModel().rows

  const getColumnCopyValue = (row: Row<T>, columnId: string, index: number) => {
    const metaCopyValue = (
      row.getVisibleCells().find((cell) => cell.column.id === columnId)?.column
        .columnDef.meta as any
    )?.copyValue

    return (
      getCopyColumnValue?.(row.original, columnId, index) ??
      (typeof metaCopyValue === 'function'
        ? metaCopyValue(row.original, index)
        : undefined) ??
      row.getValue(columnId) ??
      (row.original as any)?.[columnId]
    )
  }

  const getColumnCopyTitle = (columnId: string) => {
    const column = table.getColumn(columnId)
    const header = column?.columnDef.header

    return typeof header === 'string' ? header : columnId
  }

  const handleCopyColumns = async (columnIds: string[]) => {
    const availableColumnIds = columnIds.filter((columnId) =>
      canCopyColumn(columnId),
    )

    if (!availableColumnIds.length) return

    const lines =
      availableColumnIds.length === 1
        ? tableRows.map((row, index) => {
            const value = getColumnCopyValue(row, availableColumnIds[0], index)

            return `${index + 1} ${normalizeCopyValue(value)}`
          })
        : [
            ['N', ...availableColumnIds.map(getColumnCopyTitle)].join(
              COPY_COLUMNS_MULTI_SEPARATOR,
            ),
            ...tableRows.map((row, index) =>
              [
                index + 1,
                ...availableColumnIds.map((columnId) =>
                  normalizeCopyValue(getColumnCopyValue(row, columnId, index)),
                ),
              ].join(COPY_COLUMNS_MULTI_SEPARATOR),
            ),
          ]

    try {
      await copyTextToClipboard(lines.join('\n'))
      addAlert({
        title:
          availableColumnIds.length === 1
            ? '\u0421\u0442\u043e\u043b\u0431\u0435\u0446 \u0441\u043a\u043e\u043f\u0438\u0440\u043e\u0432\u0430\u043d'
            : '\u0421\u0442\u043e\u043b\u0431\u0446\u044b \u0441\u043a\u043e\u043f\u0438\u0440\u043e\u0432\u0430\u043d\u044b',
      })
      setCopyColumnIds([])
    } catch (error) {
      addAlert({
        title: '\u041e\u0448\u0438\u0431\u043a\u0430',
        text: '\u041d\u0435 \u0443\u0434\u0430\u043b\u043e\u0441\u044c \u0441\u043a\u043e\u043f\u0438\u0440\u043e\u0432\u0430\u0442\u044c \u0441\u0442\u043e\u043b\u0431\u0446\u044b',
        type: 'error',
      })
    }
  }

  const handleCopyColumn = (columnId: string) => {
    if (!copyColumnsMultiple) {
      handleCopyColumns([columnId])
      return
    }

    setCopyColumnIds((prev) =>
      prev.includes(columnId)
        ? prev.filter((id) => id !== columnId)
        : [...prev, columnId],
    )
  }

  const handleBodyRowDragEnd = (event: DragEndEvent) => {
    if (!rowReorder) {
      return
    }

    const { active, over } = event
    if (!over) {
      return
    }

    if (rowReorder.constrainToGroup && rowReorder.getGroupId) {
      const activeRow = tableRows.find((row) => row.id === String(active.id))
      const overRow = tableRows.find((row) => row.id === String(over.id))

      if (!activeRow || !overRow) {
        return
      }

      const activeGroupId = rowReorder.getGroupId(activeRow.original)
      const overGroupId = rowReorder.getGroupId(overRow.original)

      if ((activeGroupId ?? null) !== (overGroupId ?? null)) {
        addAlert({
          type: 'error',
          title: 'Ошибка',
          text: 'Подстроку можно перемещать только внутри своей родительской строки',
        })
        return
      }
    }

    rowReorder.onDragEnd(event)
  }

  const bodyRows = rowReorder?.active
    ? tableRows.map((row) => {
        const isSortable = rowReorder.sortableRowIds.includes(row.id)

        return (
          <DraggableBodyRow
            key={row.id}
            row={row}
            columns={columns}
            editMode={editMode}
            getRowClassName={getRowClassName}
            disabled={!isSortable}
          />
        )
      })
    : tableRows.map((row) => (
        <StaticBodyRow
          key={row.id}
          row={row}
          columns={columns}
          editMode={editMode}
          getRowClassName={getRowClassName}
        />
      ))

  // reorder columns after drag & drop
  function handleDragEnd(event: DragEndEvent) {
    if (!editMode?.active || !editMode?.mode?.includes('dnd')) {
      return
    }

    const { active, over } = event
    if (!over || active.id === over.id) {
      return
    }

    // Get indexes of active and over columns
    const oldIndex = columnOrder.indexOf(active.id as string)
    const newIndex = columnOrder.indexOf(over.id as string)
    if (oldIndex < 0 || newIndex < 0) {
      return
    }

    // Prevent moving the first column
    if (oldIndex === 0 || newIndex === 0) {
      return
    }

    // Prevent moving the last column
    // if (
    //   oldIndex === columnOrder?.length - 1 ||
    //   newIndex === columnOrder?.length - 1
    // ) {
    //   return
    // }

    editMode?.onChange({
      columnOrder: arrayMove(columnOrder, oldIndex, newIndex),
      hiddenColumns: editMode?.hiddenColumns ?? [],
    })
  }

  const changeVisibility = (id: string) => {
    const columns = editMode?.hiddenColumns ?? []

    const hiddenColumns = columns.includes(id)
      ? columns.filter((x) => x !== id)
      : [...columns, id]

    editMode?.onChange({
      columnOrder: editMode?.columnOrder ?? [],
      hiddenColumns: hiddenColumns,
    })
  }

  const tableStyle = useMemo(
    () =>
      ({
        '--ct-header-bg': headerBgColor,
        '--ct-text-align': align,
        '--ct-header-wrap': noHeaderWrap ? 'nowrap' : 'normal',
        '--ct-first-column-bg': firstColumnBg,
      } as CSSProperties),
    [headerBgColor, align, noHeaderWrap, firstColumnBg],
  )

  return (
    <DndContext
      collisionDetection={closestCenter}
      modifiers={[restrictToHorizontalAxis]}
      onDragEnd={handleDragEnd}
      sensors={editMode?.active ? sensors : []}
    >
      <div
        ref={tableWrapperRef}
        className={classNames(
          cn.tableWrapper,
          flexColumns && cn.tableWrapperFlex,
          (compact || dense) && cn.tableCompact,
          dense && cn.tableDense,
          dragScroll && cn.tableDragScroll,
          isDragScrolling && cn.tableDragScrolling,
          stickyHeader && cn.sticky,
          className,
        )}
        style={tableStyle}
        onMouseDown={handleDragScrollStart}
        onMouseMove={handleDragScrollMove}
        onMouseUp={handleDragScrollEnd}
        onMouseLeave={handleDragScrollEnd}
        onClickCapture={handleDragScrollClick}
      >
        {copyColumns && copyColumnsMultiple && copyColumnIds.length > 0 && (
          <div className={cn.copyColumnsToolbar}>
            <div className={cn.copyColumnsToolbarText}>
              Выбрано столбцов: {copyColumnIds.length}
            </div>
            <button
              type="button"
              className={cn.copyColumnsToolbarButton}
              onClick={() => handleCopyColumns(copyColumnIds)}
            >
              Скопировать выбранные
            </button>
            <button
              type="button"
              className={cn.copyColumnsToolbarButtonSecondary}
              onClick={() => setCopyColumnIds([])}
            >
              Сбросить
            </button>
          </div>
        )}
        <div
          className={classNames(
            cn.tableContent,
            overlayLoading && cn.tableContentBlur,
          )}
        >
          <table className={cn.table}>
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr
                  key={headerGroup.id}
                  className={classNames(cn.rowHeaderWrapper, cn.rowWrapper)}
                >
                  <SortableContext
                    items={columns.map((item) => item.id ?? '-')}
                    strategy={horizontalListSortingStrategy}
                  >
                    {headerGroup.headers.map((header, index) => {
                      return (
                        <DraggableTableHeader
                          key={header.id}
                          header={header}
                          editMode={editMode}
                          tooltip={
                            tooltips.find((item) => item.id === header.id)
                              ?.tooltip
                          }
                          sorting={
                            sorting.find((item) => item.key === header.id)
                              ?.order ?? null
                          }
                          onChangeVisibility={() => changeVisibility(header.id)}
                          onSorting={() => onSortingClick?.(header.id)}

                          hoverActions={hoverHeaderActions}
                          showTooltipAction={showHeaderTooltipAction}

                          canCopyColumn={
                            copyColumns && canCopyColumn(header.column.id)
                          }
                          isCopyColumnSelected={copyColumnIds.includes(
                            header.column.id,
                          )}
                          copyColumnsMultiple={copyColumnsMultiple}
                          onCopyColumn={() =>
                            handleCopyColumn(header.column.id)
                          }

                        />
                      )
                    })}
                  </SortableContext>
                </tr>
              ))}
              {!!subHeader?.items?.length && subHeader?.pinned === 'top' && (
                <SubHeaderRows<T>
                  columns={columns}
                  table={table}
                  subHeader={subHeader}
                  editMode={editMode}
                />
              )}
            </thead>
            <tbody className={cn.tableBody}>
              {rowReorder?.active ? (
                <DndContext
                  collisionDetection={closestCenter}
                  modifiers={[restrictToVerticalAxis]}
                  onDragEnd={handleBodyRowDragEnd}
                  sensors={rowReorderSensors}
                >
                  <SortableContext
                    items={tableRows.map((row) => row.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    {bodyRows}
                  </SortableContext>
                </DndContext>
              ) : (
                bodyRows
              )}
              {loading && (
                <tr>
                  <td
                    colSpan={table.getAllColumns().length}
                    className={cn.fullLineWrapper}
                  >
                    <div className={cn.loadingWrapper}>
                      <Spin
                        indicator={
                          <LoadingOutlined
                            spin
                            onPointerEnterCapture={undefined}
                            onPointerLeaveCapture={undefined}
                          />
                        }
                      />
                    </div>
                  </td>
                </tr>
              )}
              {!loading && table.getRowModel().rows.length === 0 && (
                <tr>
                  <td
                    colSpan={table.getAllColumns().length}
                    className={cn.fullLineWrapper}
                  >
                    <Empty
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description={'Данные на найдены'}
                    />
                  </td>
                </tr>
              )}
            </tbody>
            {!!subHeader?.items?.length && subHeader?.pinned === 'bottom' && (
              <SubHeader<T>
                columns={columns}
                table={table}
                subHeader={subHeader}
                editMode={editMode}
              />
            )}
          </table>
        </div>
        {overlayLoading && (
          <div className={cn.overlayLoading} aria-hidden>
            <Preloader fullScreen="center" />
          </div>
        )}
      </div>
    </DndContext>
  )
}

const StaticBodyRow = <T,>({
  row,
  columns,
  editMode,
  getRowClassName,
}: {
  row: Row<T>
  columns: ColumnDef<T, any>[]
  editMode?: IEditMode
  getRowClassName?: (row: T) => string | undefined
}) => (
  <tr className={classNames(cn.rowWrapper, getRowClassName?.(row.original))}>
    {row.getVisibleCells().map((cell) => (
      <SortableContext
        key={cell.id}
        items={columns.map((item) => item.id ?? '-')}
        strategy={horizontalListSortingStrategy}
      >
        <DragAlongCell key={cell.id} editMode={editMode} cell={cell} />
      </SortableContext>
    ))}
  </tr>
)

const DraggableBodyRow = <T,>({
  row,
  columns,
  editMode,
  getRowClassName,
  disabled = false,
}: {
  row: Row<T>
  columns: ColumnDef<T, any>[]
  editMode?: IEditMode
  getRowClassName?: (row: T) => string | undefined
  disabled?: boolean
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: row.id,
    disabled,
  })

  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.85 : 1,
    zIndex: isDragging ? 2 : 0,
    position: 'relative',
  }

  return (
    <RowDragHandleContext.Provider value={{ attributes, listeners }}>
      <tr
        ref={setNodeRef}
        style={style}
        className={classNames(cn.rowWrapper, getRowClassName?.(row.original))}
      >
        {row.getVisibleCells().map((cell) => (
          <SortableContext
            key={cell.id}
            items={columns.map((item) => item.id ?? '-')}
            strategy={horizontalListSortingStrategy}
          >
            <DragAlongCell key={cell.id} editMode={editMode} cell={cell} />
          </SortableContext>
        ))}
      </tr>
    </RowDragHandleContext.Provider>
  )
}

const SubHeader = <T,>({
  table,
  columns,
  subHeader,
  editMode,
}: {
  table: Table<T>
  columns: ColumnDef<T, any>[]
  subHeader: ISubHeader
  editMode?: IEditMode
}) => {
  return (
    <thead className="subheader">
      <SubHeaderRows<T>
        columns={columns}
        table={table}
        subHeader={subHeader}
        editMode={editMode}
      />
    </thead>
  )
}

const SubHeaderRows = <T,>({
  table,
  columns,
  subHeader,
  editMode,
}: {
  table: Table<T>
  columns: ColumnDef<T, any>[]
  subHeader: ISubHeader
  editMode?: IEditMode
}) => {
  return (
    <>
      {table.getHeaderGroups().map((headerGroup) => (
        <tr
          key={`${headerGroup.id}-subheader`}
          className={classNames(cn.rowHeaderWrapper, cn.rowWrapper)}
        >
          <SortableContext
            items={columns.map((item) => item.id ?? '-')}
            strategy={horizontalListSortingStrategy}
          >
            {headerGroup.headers.map((header) => {
              const item = subHeader.items.find(
                (item) => item.key === header.id,
              )

              return (
                <TableSubHeader
                  key={header.id}
                  header={header}
                  editMode={editMode}
                  value={item?.value ?? ''}
                />
              )
            })}
          </SortableContext>
        </tr>
      ))}
    </>
  )
}

const TableSubHeader = ({
  header,
  value,
  editMode,
}: {
  header: Header<any, unknown>
  value: string
  editMode?: IEditMode
}) => {
  const isDnDMode =
    editMode?.mode?.includes('dnd') &&
    editMode?.active &&
    editMode.editOrderKey?.includes(header.id)

  const { attributes, isDragging, listeners, setNodeRef, transform } =
    useSortable({
      id: header.column.id,
      disabled: !isDnDMode,
    })

  const isPinned = header.column.getIsPinned()

  const style: CSSProperties = {
    opacity: isDragging ? 0.8 : 1,
    position: isPinned ? 'sticky' : 'relative',
    transform: editMode?.active ? CSS.Translate.toString(transform) : '',
    transition: 'width transform 0.2s ease-in-out',
    width: header.column.getSize(),
    cursor: 'default',
    zIndex: isPinned || isDragging ? 1 : 0,
    left:
      isPinned === 'left' ? `${header.column.getStart('left')}px` : undefined,
    right:
      isPinned === 'right' ? `${header.column.getAfter('right')}px` : undefined,
  }

  return (
    <th
      className={classNames(
        cn.subHeaderCell,
        editMode?.hiddenColumns?.includes(header.column.id) && cn.cellHidden,
      )}
      colSpan={header.colSpan}
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
    >
      {value}
    </th>
  )
}

const DraggableTableHeader = ({
  header,
  editMode,
  tooltip = '',
  sorting = undefined,
  onSorting = () => {},
  onChangeVisibility = () => {},

  hoverActions = false,
  showTooltipAction = true,

  canCopyColumn = false,
  isCopyColumnSelected = false,
  copyColumnsMultiple = false,
  onCopyColumn = () => {},

}: {
  header: Header<any, unknown>
  tooltip?: string
  sorting?: 'none' | 'asc' | 'desc' | null
  onSorting?: () => void
  editMode?: IEditMode
  onChangeVisibility?: () => void

  hoverActions?: boolean
  showTooltipAction?: boolean

  canCopyColumn?: boolean
  isCopyColumnSelected?: boolean
  copyColumnsMultiple?: boolean
  onCopyColumn?: () => void

}) => {
  const [isActionsMenuOpen, setIsActionsMenuOpen] = useState(false)
  const [actionsMenuPosition, setActionsMenuPosition] = useState<{
    top: number
    right: number
  } | null>(null)
  const actionsMenuButtonRef = useRef<HTMLDivElement | null>(null)
  const isDnDMode =
    editMode?.mode?.includes('dnd') &&
    editMode?.active &&
    editMode.editOrderKey?.includes(header.id)
  const isVisibilityChangeMode =
    editMode?.mode?.includes('visibility') &&
    editMode?.active &&
    editMode.editVisibilityKey?.includes(header.id)

  const { attributes, isDragging, listeners, setNodeRef, transform } =
    useSortable({
      id: header.column.id,
      disabled: !isDnDMode,
    })

  const isPinned = header.column.getIsPinned()

  const style: CSSProperties = {
    opacity: isDragging ? 0.8 : 1,
    position: isPinned ? 'sticky' : 'relative',
    transform: isDnDMode ? CSS.Translate.toString(transform) : '', // translate instead of transform to avoid squishing
    transition: 'width transform 0.2s ease-in-out',
    width: header.column.getSize(),
    cursor: 'default',
    zIndex: isPinned || isDragging ? 1 : 0,
    left:
      isPinned === 'left' ? `${header.column.getStart('left')}px` : undefined,
    right:
      isPinned === 'right' ? `${header.column.getAfter('right')}px` : undefined,
  }

  const getCopyTitle = () =>
    copyColumnsMultiple
      ? isCopyColumnSelected
        ? '\u0423\u0431\u0440\u0430\u0442\u044c \u0441\u0442\u043e\u043b\u0431\u0435\u0446 \u0438\u0437 \u043a\u043e\u043f\u0438\u0440\u043e\u0432\u0430\u043d\u0438\u044f'
        : '\u0412\u044b\u0431\u0440\u0430\u0442\u044c \u0441\u0442\u043e\u043b\u0431\u0435\u0446 \u0434\u043b\u044f \u043a\u043e\u043f\u0438\u0440\u043e\u0432\u0430\u043d\u0438\u044f'
      : '\u0421\u043a\u043e\u043f\u0438\u0440\u043e\u0432\u0430\u0442\u044c \u0441\u0442\u043e\u043b\u0431\u0435\u0446'

  const getSortingTitle = () => {
    if (sorting === 'asc') return '\u0421\u043e\u0440\u0442\u0438\u0440\u043e\u0432\u043a\u0430: \u043f\u043e \u0432\u043e\u0437\u0440\u0430\u0441\u0442\u0430\u043d\u0438\u044e'
    if (sorting === 'desc') return '\u0421\u043e\u0440\u0442\u0438\u0440\u043e\u0432\u043a\u0430: \u043f\u043e \u0443\u0431\u044b\u0432\u0430\u043d\u0438\u044e'
    return '\u0412\u043a\u043b\u044e\u0447\u0438\u0442\u044c \u0441\u043e\u0440\u0442\u0438\u0440\u043e\u0432\u043a\u0443'
  }

  const copyAction: HeaderAction | null = !editMode?.active && canCopyColumn
    ? {
        key: 'copy',
        title: getCopyTitle(),
        icon: (
          <CopyOutlined
            onPointerEnterCapture={undefined}
            onPointerLeaveCapture={undefined}
          />
        ),
        active: isCopyColumnSelected,
        onClick: onCopyColumn,
      }
    : null

  const sortIcon =
    sorting === 'desc' ? (
      <SortDescendingOutlined
        onPointerEnterCapture={undefined}
        onPointerLeaveCapture={undefined}
      />
    ) : (
      <SortAscendingOutlined
        onPointerEnterCapture={undefined}
        onPointerLeaveCapture={undefined}
      />
    )

  const sortingAction: HeaderAction | null = !editMode?.active && sorting
    ? {
        key: 'sorting',
        title: getSortingTitle(),
        icon: sortIcon,
        active: sorting !== 'none',
        onClick: onSorting,
      }
    : null

  const dndAction: HeaderAction | null = editMode?.active && isDnDMode
    ? {
        key: 'dnd',
        title: '\u041f\u0435\u0440\u0435\u0442\u0430\u0449\u0438\u0442\u0435 \u0437\u0430\u0433\u043e\u043b\u043e\u0432\u043e\u043a, \u0447\u0442\u043e\u0431\u044b \u0438\u0437\u043c\u0435\u043d\u0438\u0442\u044c \u043f\u043e\u0440\u044f\u0434\u043e\u043a',
        icon: (
          <SwapOutlined
            onPointerEnterCapture={null}
            onPointerLeaveCapture={null}
          />
        ),
      }
    : null

  const visibilityAction: HeaderAction | null =
    editMode?.active && isVisibilityChangeMode
    ? {
        key: 'visibility',
        title: '\u0421\u043a\u0440\u044b\u0442\u044c \u0441\u0442\u043e\u043b\u0431\u0435\u0446',
        icon: (
          <EyeInvisibleOutlined
            onPointerEnterCapture={null}
            onPointerLeaveCapture={null}
          />
        ),
        onClick: onChangeVisibility,
      }
    : null

  const infoAction: HeaderAction | null =
    showTooltipAction && tooltip && !editMode?.active
    ? {
        key: 'info',
        title: '\u041f\u043e\u0434\u0441\u043a\u0430\u0437\u043a\u0430',
        tooltip,
        icon: (
          <InfoCircleOutlined
            onPointerEnterCapture={undefined}
            onPointerLeaveCapture={undefined}
          />
        ),
      }
    : null

  const headerActions: HeaderAction[] = [
    copyAction,
    sortingAction,
    dndAction,
    visibilityAction,
    infoAction,
  ].filter((action): action is HeaderAction => Boolean(action))
  const shouldCollapseActions = headerActions.length > 2
  const isHeaderActionsActive = headerActions.some((action) => action.active)

  const updateActionsMenuPosition = () => {
    const buttonRect = actionsMenuButtonRef.current?.getBoundingClientRect()

    if (!buttonRect) return

    setActionsMenuPosition({
      top: buttonRect.bottom + 4,
      right: window.innerWidth - buttonRect.right,
    })
  }

  const handleActionClick = (
    event: React.MouseEvent,
    action: HeaderAction,
  ) => {
    event.stopPropagation()
    event.preventDefault()

    action.onClick?.()
    setIsActionsMenuOpen(false)
    setActionsMenuPosition(null)
  }

  const renderActionIcon = (action: HeaderAction) => (
    <div
      key={action.key}
      className={classNames(
        cn.headerCellActionIcon,
        action.active && cn.headerCellActionIconActive,
      )}
      title={action.tooltip || action.title}
      onPointerDown={(e) => {
        e.stopPropagation()
        e.preventDefault()
      }}
      onClick={(e) => handleActionClick(e, action)}
    >
      {action.icon}
    </div>
  )

  return (
    <th
      data-column-id={header.column.id}
      className={classNames(
        cn.headerCell,
        hoverActions && cn.headerCellHoverActions,
        editMode?.hiddenColumns?.includes(header.column.id) && cn.cellHidden,
      )}
      colSpan={header.colSpan}
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
    >
      <TooltipWrapper title={tooltip}>
        <div
          className={cn.hc}
          style={{ display: 'flex', alignItems: 'center', height: '100%' }}
        >
          <div className={cn.headerContent}>
            {header.isPlaceholder
              ? null
              : flexRender(header.column.columnDef.header, header.getContext())}
          </div>
          {headerActions.length > 0 && (
            <div
              className={cn.headerCellActions}
              onMouseLeave={() => {
                setIsActionsMenuOpen(false)
                setActionsMenuPosition(null)
              }}
            >
              {shouldCollapseActions ? (
                <div className={cn.headerActionsMenuWrapper}>
                  <div
                    ref={actionsMenuButtonRef}
                    className={classNames(
                      cn.headerCellActionIcon,
                      (isHeaderActionsActive || isActionsMenuOpen) &&
                        cn.headerCellActionIconActive,
                    )}
                    title="\u0414\u0435\u0439\u0441\u0442\u0432\u0438\u044f"
                    onPointerDown={(e) => {
                      e.stopPropagation()
                      e.preventDefault()
                    }}
                    onClick={(e) => {
                      e.stopPropagation()
                      e.preventDefault()
                      setIsActionsMenuOpen((prev) => {
                        if (prev) {
                          setActionsMenuPosition(null)
                          return false
                        }

                        updateActionsMenuPosition()
                        return true
                      })
                    }}
                  >
                    <MoreOutlined
                      onPointerEnterCapture={undefined}
                      onPointerLeaveCapture={undefined}
                    />
                  </div>
                  {isActionsMenuOpen && actionsMenuPosition && (
                    <div
                      className={cn.headerActionsMenu}
                      style={actionsMenuPosition}
                    >
                      {headerActions.map((action) => {
                        const row = (
                          <div
                            key={action.key}
                            className={classNames(
                              cn.headerActionsMenuRow,
                              action.active && cn.headerActionsMenuRowActive,
                              !action.onClick && cn.headerActionsMenuRowStatic,
                            )}
                            onPointerDown={(e) => {
                              e.stopPropagation()
                              e.preventDefault()
                            }}
                            onClick={(e) => handleActionClick(e, action)}
                          >
                            <span className={cn.headerActionsMenuIcon}>
                              {action.icon}
                            </span>
                            <span className={cn.headerActionsMenuText}>
                              {action.title}
                            </span>
                          </div>
                        )

                        return action.tooltip ? (
                          <TooltipWrapper key={action.key} title={action.tooltip}>
                            {row}
                          </TooltipWrapper>
                        ) : (
                          row
                        )
                      })}
                    </div>
                  )}
                </div>
              ) : (
                headerActions.map((action) => renderActionIcon(action))
              )}
            </div>
          )}
        </div>
      </TooltipWrapper>
    </th>
  )
}

interface DragAlongCellProps<T> {
  cell: Cell<T, unknown>
  editMode?: IEditMode
  onClick?: () => void
}

export function DragAlongCell<T>({
  cell,
  editMode,
  onClick,
}: DragAlongCellProps<T>) {
  const isDnDMode =
    editMode?.mode?.includes('dnd') &&
    editMode?.active &&
    editMode.editOrderKey?.includes(cell.column.id)

  const { isDragging, setNodeRef, transform } = useSortable({
    id: cell.column.id,
    disabled: !isDnDMode,
  })

  const isPinned = cell.column.getIsPinned()
  const start = cell.column.getStart?.(isPinned || undefined)

  const style: CSSProperties = {
    opacity: isDragging ? 0.8 : 1,
    position: isPinned ? 'sticky' : 'relative',
    transform: isDnDMode && transform ? CSS.Translate.toString(transform) : '',
    transition: 'width transform 0.2s ease-in-out',
    width: cell.column.getSize(),
    zIndex: isPinned || isDragging ? 1 : 0,
    left:
      isPinned === 'left' && typeof start === 'number'
        ? `${start}px`
        : undefined,
    right:
      isPinned === 'right' ? `${cell.column.getAfter('right')}px` : undefined,
    cursor: isDnDMode ? 'grab' : 'default',
  }

  return (
    <td
      data-column-id={cell.column.id}
      ref={setNodeRef}
      onClick={onClick}
      style={style}
      className={classNames(
        cn.tableCell,
        editMode?.hiddenColumns?.includes(cell.column.id) && cn.cellHidden,
        isDragging && cn.cellDragging,
      )}
    >
      {flexRender(cell.column.columnDef.cell, cell.getContext())}
    </td>
  )
}
export default CustomTable
